import { ACADEMY_COURSE_IDS } from "../../onboarding-v2/core";
import { courseKey } from "../../onboarding-v2/app/messages";

export const buildOnboardingMessages = (t) => ({
  trySearch: t("navigationV2.onboarding.trySearch"),
  demoDescription: t("navigationV2.onboarding.demoDescription"),
  pinHint: t("navigationV2.onboarding.pinHint"),
  pinGuideHint: t("navigationV2.onboarding.pinGuideHint"),
  pinGuideSuccess: t("navigationV2.onboarding.pinGuideSuccess"),
  guideLabel: t("navigationV2.destinations.assistance.getting-started.label"),
  completed: t("navigationV2.onboarding.completed"),
  selected: (label) => t("navigationV2.onboarding.selected", { label }),
  shortcutOpened: (label) => t("navigationV2.onboarding.shortcutOpened", { label }),
  samples: [
    { id: "animals", label: t("navigationV2.onboarding.searchResult"), icon: "search" },
    { id: "enclosures", label: t("navigationV2.search.groups.enclosures"), icon: "search" },
    { id: "documentation", label: t("navigationV2.onboarding.helpPath").split(" → ")[0], icon: "help" },
  ],
  welcome: t("navigationV2.onboarding.welcome"),
  intro: t("navigationV2.onboarding.intro"),
  replay: t("navigationV2.onboarding.replay"),
  skip: t("navigationV2.onboarding.skip"),
  next: t("navigationV2.onboarding.next"),
  finish: t("navigationV2.onboarding.finish"),
  back: t("navigationV2.onboarding.back"),
  demo: t("navigationV2.onboarding.demo"),
  clear: t("navigationV2.onboarding.clear"),
  searchLabel: t("navigationV2.onboarding.searchLabel"),
  searchPlaceholder: t("navigationV2.onboarding.searchPlaceholder"),
  searchResult: t("navigationV2.onboarding.searchResult"),
  noResults: t("navigationV2.onboarding.noResults"),
  searchSuccess: t("navigationV2.onboarding.searchSuccess"),
  pin: t("navigationV2.onboarding.pin"),
  unpin: t("navigationV2.onboarding.unpin"),
  pinned: t("navigationV2.onboarding.pinned"),
  pinEmpty: t("navigationV2.onboarding.pinEmpty"),
  pinSuccess: t("navigationV2.onboarding.pinSuccess"),
  example: t("navigationV2.onboarding.example"),
  hover: t("navigationV2.onboarding.hover"),
  tooltip: t("navigationV2.onboarding.tooltip"),
  themeHint: t("navigationV2.onboarding.themeHint"),
  helpPath: t("navigationV2.onboarding.helpPath"),
  helpQuestion: t("navigationV2.onboarding.helpQuestion"),
  progress: (values) => t("navigationV2.onboarding.progress", values),
  steps: [0, 1, 2, 3, 4].map((index) => ({
    label: t(`navigationV2.onboarding.steps.${index}.label`),
    title: t(`navigationV2.onboarding.steps.${index}.title`),
    description: t(`navigationV2.onboarding.steps.${index}.description`),
  })),
  themeLabels: Object.fromEntries(["foret", "savane", "ocean", "mineral", "nuit", "contraste", "daltonien", "brume", "ardoise", "craie"].map((id) => [id, t(`navigationV2.header.theme.${id}`)])),
});

const THEME_IDS = ["foret", "savane", "ocean", "mineral", "nuit", "contraste", "daltonien", "brume", "ardoise", "craie"];

export const buildWelcomeMessages = (t) => ({
  title: t("navigationV2.welcome.title"),
  intro: t("navigationV2.welcome.intro"),
  skip: t("navigationV2.welcome.skip"),
  next: t("navigationV2.welcome.next"),
  back: t("navigationV2.welcome.back"),
  openGuide: t("navigationV2.welcome.openGuide"),
  startTour: t("navigationV2.welcome.startTour"),
  exploreAlone: t("navigationV2.welcome.exploreAlone"),
  progress: (values) => t("navigationV2.welcome.progress", values),
  themeHint: t("navigationV2.welcome.themeHint"),
  guideWhere: t("navigationV2.welcome.guideWhere"),
  guideCounter: t("navigationV2.welcome.guideCounter"),
  documentationWhere: t("navigationV2.welcome.documentationWhere"),
  documentationDescription: t("navigationV2.welcome.documentationDescription"),
  tryHint: t("navigationV2.welcome.tryHint"),
  tipsProgress: ({ count, total }) => t("navigationV2.welcome.tipsProgress", { count, total }),
  mobileTryout: t("navigationV2.welcome.mobileTryout"),
  tourRecommended: t("navigationV2.welcome.tourRecommended"),
  tourTitle: t("navigationV2.tours.catalogue.interfaceDiscover.title"),
  tourDescription: t("navigationV2.tours.catalogue.interfaceDiscover.description"),
  guideThemes: t("navigationV2.welcome.guideThemes"),
  // Les thèmes de « Bien démarrer », dans leur ordre, avec leurs titres de la page.
  courses: ACADEMY_COURSE_IDS.map((id) => ({ id, title: t(`${courseKey(id)}.title`) })),
  // Les exercices sont ceux de la visite de migration, avec leurs textes.
  tryouts: buildOnboardingMessages(t),
  steps: ["theme", "tips", "learn"].map((id) => ({
    id,
    label: t(`navigationV2.welcome.steps.${id}.label`),
    title: t(`navigationV2.welcome.steps.${id}.title`),
    description: t(`navigationV2.welcome.steps.${id}.description`),
  })),
  tips: ["search", "pin", "hover", "mobile"].map((id) => ({
    id,
    title: t(`navigationV2.welcome.tips.${id}.title`),
    description: t(`navigationV2.welcome.tips.${id}.description`),
  })),
  themeLabels: Object.fromEntries(THEME_IDS.map((id) => [id, t(`navigationV2.header.theme.${id}`)])),
});

export const buildInstallMessages = (t) => ({
  title: t("navigationV2.installation.title"),
  description: t("navigationV2.installation.description"),
  install: t("navigationV2.installation.install"),
  how: t("navigationV2.installation.how"),
  later: t("navigationV2.installation.later"),
  error: t("navigationV2.installation.error"),
  iosTitle: t("navigationV2.installation.iosTitle"),
  iosSteps: ["menu", "add", "confirm"].map(step => t(`navigationV2.installation.iosSteps.${step}`)),
  understood: t("navigationV2.installation.understood"),
  close: t("common.close"),
});

export const buildNavigationMessages = (t) => ({
  installation: buildInstallMessages(t),
  onboarding: buildOnboardingMessages(t),
  welcome: buildWelcomeMessages(t),
  shell: {
    applicationName: t("navigationV2.shell.applicationName"),
    productName: t("navigationV2.shell.productName"),
    labEdition: t("navigationV2.shell.labEdition"),
    skipToContentLabel: t("navigationV2.shell.skipToContent"),
    openNavigationLabel: t("navigationV2.shell.openNavigation"),
    closeNavigationLabel: t("navigationV2.shell.closeNavigation"),
    mainContentLabel: t("navigationV2.shell.mainContent"),
    // Messages courts après un enregistrement (« Groupe créé »), communs aux vues V2.
    notificationsLabel: t("navigationV2.shell.notifications"),
    closeNotificationLabel: t("navigationV2.shell.closeNotification"),
  },
  menu: {
    navigationLabel: t("navigationV2.menu.navigation"),
    previewUnavailableBadge: t("navigationV2.menu.previewUnavailableBadge"),
    previewUnavailableLabel: t("navigationV2.menu.previewUnavailable"),
    pinnedHeading: t("navigationV2.menu.pinnedHeading"),
    pinnedEmptyLabel: t("navigationV2.menu.pinnedEmpty"),
    pinItemLabel: t("navigationV2.menu.pinItem"),
    unpinItemLabel: t("navigationV2.menu.unpinItem"),
    movePinnedUpLabel: t("navigationV2.menu.movePinnedUp"),
    movePinnedDownLabel: t("navigationV2.menu.movePinnedDown"),
    autoCollapseLabel: t("navigationV2.menu.autoCollapse"),
    reservedAreaHeading: t("navigationV2.menu.reservedAreaHeading"),
  },
  search: {
    label: t("navigationV2.search.label"),
    placeholder: t("navigationV2.search.placeholder"),
    resultsLabel: t("navigationV2.search.results"),
    noResultsLabel: t("navigationV2.search.noResults"),
    searchingLabel: t("navigationV2.search.searching"),
    groups: {
      navigation: t("navigationV2.search.groups.navigation"),
      animals: t("navigationV2.search.groups.animals"),
      enclosures: t("navigationV2.search.groups.enclosures"),
      sectors: t("navigationV2.search.groups.sectors"),
      campaigns: t("navigationV2.search.groups.campaigns"),
      enrichments: t("navigationV2.search.groups.enrichments"),
      groups: t("navigationV2.search.groups.groups"),
      tours: t("navigationV2.search.groups.tours"),
      users: t("navigationV2.search.groups.users"),
      taxons: t("navigationV2.search.groups.taxons"),
      resources: t("navigationV2.search.groups.resources"),
    },
    resultsCount: ({ count }) => t("navigationV2.search.resultsCount", { count }),
  },
  breadcrumbs: {
    navigationLabel: t("navigationV2.breadcrumbs.navigation"),
  },
  discardConfirmation: {
    title: t("navigationV2.discardConfirmation.title"),
    description: t("navigationV2.discardConfirmation.description"),
    cancelLabel: t("navigationV2.discardConfirmation.cancel"),
    closeLabel: t("navigationV2.discardConfirmation.close"),
    confirmLabel: t("navigationV2.discardConfirmation.confirm"),
    pendingLabel: t("navigationV2.discardConfirmation.pending"),
    retryLabel: t("navigationV2.discardConfirmation.retry"),
    failedFeedback: t("navigationV2.discardConfirmation.feedback.failed"),
    navigationPendingFeedback: t(
      "navigationV2.discardConfirmation.feedback.navigationPending"
    ),
    operationPendingFeedback: t(
      "navigationV2.discardConfirmation.feedback.operationPending"
    ),
    stateChangedFeedback: t(
      "navigationV2.discardConfirmation.feedback.stateChanged"
    ),
    unavailableFeedback: t(
      "navigationV2.discardConfirmation.feedback.unavailable"
    ),
  },
  versionSwitch: {
    label: t("navigationV2.versionSwitch.label"),
    v1Label: t("navigationV2.versionSwitch.v1"),
    v2Label: t("navigationV2.versionSwitch.v2"),
    pendingLabel: t("navigationV2.versionSwitch.pending"),
    unavailableLabel: t("navigationV2.versionSwitch.unavailable"),
    switchTo: ({ versionLabel }) => t("navigationV2.versionSwitch.switchTo", { versionLabel }),
  },
});

export const buildNavigationLocale = (t, language) => ({
  cacheKey: language || "unknown",
  text: (key) => t(key),
});
